import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Icon, ModalIsland } from '@saviynt/design-system';

import config from '../../../../config/obNextGenConfig';
import { useLocalization } from '../../../../hooks';
import { useTypedSelector } from '../../../../hooks/use-typedselector';
import { type RootState } from '../../../../services/serviceTypes';
import { resetDiscoveryFileUploadState } from '../../data-store/DiscoveryFileUploadSlice';
import FileUploadModalContent from '../FileUploadModalContent/FileUploadModalContent';
import FileUploadModalFooter from '../FileUploadModalFooter/FileUploadModalFooter';
import FileUploadPreviewTableContent from '../FileUploadPreviewTableContent/FileUploadPreviewTableContent';
import FileUploadPreviewTableFooter from '../FileUploadPreviewTableFooter/FileUploadPreviewTableFooter';
import StartImportModalContent from '../StartImportModalContent/StartImportModalContent';

import './FileUploadModal.css';

interface FileUploadModalProps {
  name: string;
  isModalOpen: boolean;
  fileUploadSourceAppId: number;
  fileUploadIobAppId: string | number | null;
  handleCloseModal: () => void;
}

/**
 * File Upload Modal Component
 */
function FileUploadModal({
  name,
  isModalOpen,
  fileUploadSourceAppId,
  fileUploadIobAppId,
  handleCloseModal,
}: FileUploadModalProps) {
  const modalTitleId = `modal-title-${name}`;
  const modalDescId = `modal-desc-${name}`;

  const dispatch = useDispatch();
  const history = useHistory();
  const formatMessage = useLocalization();

  const { isPreviewTable, isStartImport, startImportResponse } =
    useTypedSelector((state: RootState) => ({
      isPreviewTable: state.discoveryFileUpload.isPreviewTable,
      isStartImport: state.discoveryFileUpload.isStartImport,
      startImportResponse: state.discoveryFileUpload.startImportResponse,
    }));

  let contentBody: React.ReactNode = <FileUploadModalContent />;
  let footer: React.ReactNode = (
    <FileUploadModalFooter fileUploadSourceAppId={fileUploadSourceAppId} />
  );

  if (isStartImport) {
    contentBody = (
      <StartImportModalContent
        fileUploadIobAppId={fileUploadIobAppId}
        fileUploadSourceAppId={fileUploadSourceAppId}
        handleCloseModal={handleCloseModal}
      />
    );
    footer = undefined;
  } else if (isPreviewTable) {
    contentBody = <FileUploadPreviewTableContent />;
    footer = (
      <FileUploadPreviewTableFooter
        fileUploadIobAppId={fileUploadIobAppId}
        fileUploadSourceAppId={fileUploadSourceAppId}
      />
    );
  }

  return (
    <ModalIsland
      kind='default'
      onClose={() => {
        if (isStartImport && startImportResponse?.status === 200) {
          handleCloseModal();
          dispatch(resetDiscoveryFileUploadState());
          history.push(config.URL_OB_NEXT_GEN);
        } else {
          handleCloseModal();
        }
      }}
      isOpen={isModalOpen}
      title={formatMessage(
        'INTEGRATIONS.FILEUPLOAD.MODAL.TITLE',
        'Import Discovery Source'
      )}
      className={
        isPreviewTable && !isStartImport
          ? 'AOB-ModalIsland-header'
          : 'FileUploadModal'
      }
      aria-labelledby={modalTitleId}
      aria-describedby={modalDescId}
      headerIcon={<Icon kind='Connect' size='large' aria-hidden='true' />}
      contentBody={contentBody}
      FooterActionBarComp={footer}
    />
  );
}

export default FileUploadModal;
